import supabase from '../supabaseClient'
import {
  beginSignupIntake,
  clearDraft,
  readDraft,
  writeDraft,
} from '../components/signup/signupState'
import { communityHomeRoute } from '../lib/communitySetupMode'
import { persistCommunityContext } from './communityAccessCache'
import { persistAcademyContext } from './academyAccessCache'
import { readLastPortal, writeLastPortal } from './lastPortal'
import {
  clearAcademyJoinId,
  peekAcademyJoinId,
  academyInviteSignInPath,
} from '../lib/academyInvite'
import {
  buildAcademyLeagueJoinPath,
  peekAcademyLeagueJoinToken,
} from '../lib/academyLeagueJoin'
import { waitForAuthSession } from './waitForAuthSession'
import { clearQuickUploadOpen, quickUploadDialogRequested, quickUploadWantsPayment, readQuickUploadIntent } from '../lib/quickUpload'
import { HUB_HOME, fetchCommunityPlatform, isBasicCommunity } from '../lib/hub'

export function isCommunityManager(profile) {
  if (!profile) return false
  const v = profile.community_manager
  return v === true || String(v ?? '').trim().toLowerCase() === 'true'
}

export function isCoach(profile) {
  if (!profile) return true
  return String(profile.coach ?? '').trim().toLowerCase() === 'true'
}

export function isVenueOwner(profile) {
  return !!String(profile?.venue_owner || '').trim()
}

export function profileHasPhone(profile) {
  return !!(profile?.phone && String(profile.phone).trim().length > 0)
}

/** User is tied to academy or venue — must not be funnelled into community signup. */
export function hasNonCommunityPortalAffinity(profile) {
  if (!profile) return false
  if (profile.community_id && isCommunityManager(profile)) return false
  if (profile.academy_id) return true
  if (isVenueOwner(profile)) return true
  if (profile.admin_Pass) return true
  return false
}

/**
 * Org setup via /signup/* — same as Try Statbox. Phone is collected later in
 * the wizard (e.g. WhatsApp on policies), not on /verify-phone first.
 *
 * Do not require community_manager / coach flags here — those are often unset
 * until the wizard finishes, but the user already signed in on that portal.
 */
export function needsSignupIntakeOnly(portal, profile) {
  if (portal === 'community') {
    if (!profile) return true
    return !profile.community_id
  }
  if (portal === 'academy') {
    if (!profile) return true
    return !profile.academy_id
  }
  if (portal === 'venue') {
    if (!profile) return true
    return !isVenueOwner(profile)
  }
  return false
}

/** Remember portal context so /verify-phone can route back after OTP. */
export function stashPhoneReturn(portal) {
  sessionStorage.setItem('signup_return_to', portal)
  writeLastPortal(portal)
}

/** Portal hint stashed at login; falls back to last portal in localStorage. */
export function consumePortalReturnHint() {
  let portal =
    typeof window !== 'undefined'
      ? sessionStorage.getItem('signup_return_to')
      : null
  if (typeof window !== 'undefined' && portal) {
    sessionStorage.removeItem('signup_return_to')
  }
  if (!portal) portal = readLastPortal()
  return portal
}

const USERS_PORTAL_SELECT =
  'phone, community_id, academy_id, name, community_manager, coach, venue_owner, academies(name)'

export const USERS_LOGIN_SELECT = `${USERS_PORTAL_SELECT}, admin_Pass`

export function profileCanAccessPortal(profile, portal) {
  if (portal === 'community') {
    return !!(profile?.community_id && isCommunityManager(profile))
  }
  if (portal === 'academy') {
    return !!(profile?.academy_id && isCoach(profile))
  }
  if (portal === 'venue') {
    return isVenueOwner(profile)
  }
  if (portal === 'admin') {
    return !!profile?.admin_Pass
  }
  return false
}

/** Portals the profile can enter right now, in display order. */
export function accessiblePortals(profile) {
  return [...PORTAL_FALLBACK_ORDER, 'admin'].filter((p) => profileCanAccessPortal(profile, p))
}

export async function routePortalAfterAuth(navigate, profile, portal, opts) {
  if (portal === 'admin') {
    navigate('/admin/video-analysis', { replace: true })
    return true
  }
  if (portal === 'community') {
    return routeCommunityPortalAfterAuth(navigate, profile, opts)
  }
  if (portal === 'academy') {
    return routeAcademyPortalAfterAuth(navigate, profile, opts)
  }
  if (portal === 'venue') {
    return routeVenuePortalAfterAuth(navigate, profile, opts)
  }
  return false
}

const PORTAL_FALLBACK_ORDER = ['community', 'academy', 'venue']

function profileWithVerifiedPhone(profile, verifiedPhone) {
  if (!profile) return profile
  if (verifiedPhone && !profileHasPhone(profile)) {
    return { ...profile, phone: verifiedPhone }
  }
  return profile
}

/**
 * After OTP succeeds, route back to the portal the user signed into.
 * @returns {Promise<boolean>} true if navigation was handled
 */
export async function routePortalAfterPhoneVerify(
  navigate,
  userId,
  { verifiedPhone } = {},
) {
  const portal = consumePortalReturnHint()

  const { data: profile, error: profileError } = await supabase
    .from('Users')
    .select(USERS_PORTAL_SELECT)
    .eq('user_id', userId)
    .maybeSingle()

  const profileRow = profileError ? null : profileWithVerifiedPhone(profile, verifiedPhone)

  if (portal === 'community') {
    const handled = await routeCommunityPortalAfterAuth(navigate, profileRow, {
      userId,
      skipPhoneCheck: true,
    })
    if (handled) return true
    await navigateToSignupIntake(navigate, 'community')
    return true
  }

  if (portal === 'academy') {
    const handled = await routeAcademyPortalAfterAuth(navigate, profileRow, {
      userId,
      skipPhoneCheck: true,
    })
    if (handled) return true
    await navigateToSignupIntake(navigate, 'academy')
    return true
  }

  if (portal === 'venue') {
    const handled = await routeVenuePortalAfterAuth(navigate, profileRow, {
      userId,
      skipPhoneCheck: true,
    })
    if (handled) return true
    await navigateToSignupIntake(navigate, 'venue')
    return true
  }

  if (profileRow?.community_id && isCommunityManager(profileRow)) {
    const handled = await routeCommunityPortalAfterAuth(navigate, profileRow, {
      userId,
      skipPhoneCheck: true,
    })
    if (handled) return true
  }

  if (profileRow?.academy_id && isCoach(profileRow)) {
    const handled = await routeAcademyPortalAfterAuth(navigate, profileRow, {
      userId,
      skipPhoneCheck: true,
    })
    if (handled) return true
  }

  if (isVenueOwner(profileRow)) {
    const handled = await routeVenuePortalAfterAuth(navigate, profileRow, {
      userId,
      skipPhoneCheck: true,
    })
    if (handled) return true
  }

  return false
}

/**
 * Send an authenticated portal user into the same funnel as "Try Statbox"
 * (/signup), at the persona-specific intake step — not the legacy
 * create-community / academy-pending screens.
 */
export async function navigateToSignupIntake(navigate, persona, { replace = true } = {}) {
  const draft = readDraft()
  const patch = {
    persona,
    signupStep: Math.max(Number(draft.signupStep) || 0, 2),
  }

  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) {
      const meta = session.user.user_metadata || {}
      patch.email = session.user.email || draft.email || ''
      patch.fullName =
        meta.full_name ||
        meta.name ||
        draft.fullName ||
        ''
    }
  } catch (err) {
    console.warn('navigateToSignupIntake: could not hydrate draft from session', err)
  }

  writeDraft({ ...draft, ...patch })
  navigate(beginSignupIntake(persona), { replace })
}

/**
 * When a logged-in user hits a public page (e.g. /), send them to the portal
 * that matches their profile or last-used portal hint.
 * @returns {Promise<boolean>} true if navigation was handled
 */
export async function routeLoggedInUserFromPublicPage(navigate) {
  const session = await waitForAuthSession()
  if (!session?.user?.id) return false

  const userId = session.user.id
  const { data: profile, error: profileError } = await supabase
    .from('Users')
    .select(`${USERS_PORTAL_SELECT}, admin_Pass`)
    .eq('user_id', userId)
    .maybeSingle()

  if (profileError || !profile) return false

  const canEnterPortal = !!(
    profile.admin_Pass
    || profileCanAccessPortal(profile, 'community')
    || profileCanAccessPortal(profile, 'academy')
    || profileCanAccessPortal(profile, 'venue')
  )
  if (quickUploadDialogRequested() && !canEnterPortal) return false
  if (canEnterPortal) clearQuickUploadOpen()

  const opts = { blockAutoRedirect: false, userId }

  if (profile.admin_Pass) {
    navigate('/admin/video-analysis', { replace: true })
    return true
  }

  const lastPortal = readLastPortal()

  // Prefer the portal the user last signed into when their profile still grants access.
  if (lastPortal && profileCanAccessPortal(profile, lastPortal)) {
    const handled = await routePortalAfterAuth(navigate, profile, lastPortal, opts)
    if (handled) return true
  }

  // Any other portal on the profile (e.g. user belongs to multiple workspaces).
  for (const portal of PORTAL_FALLBACK_ORDER) {
    if (portal === lastPortal) continue
    if (!profileCanAccessPortal(profile, portal)) continue
    const handled = await routePortalAfterAuth(navigate, profile, portal, opts)
    if (handled) return true
  }

  // Mid-signup — no portal assigned yet; honor last sign-in for intake routing.
  if (lastPortal) {
    const handled = await routePortalAfterAuth(navigate, profile, lastPortal, opts)
    if (handled) return true
  }

  return false
}

/** @returns {Promise<boolean>} true if navigation was handled */
export async function routeCommunityPortalAfterAuth(
  navigate,
  profile,
  { blockAutoRedirect = false, userId, skipPhoneCheck = false } = {},
) {
  if (hasNonCommunityPortalAffinity(profile)) {
    return false
  }

  if (needsSignupIntakeOnly('community', profile)) {
    writeLastPortal('community')
    await navigateToSignupIntake(navigate, 'community')
    return true
  }

  if (profile?.community_id && isCommunityManager(profile)) {
    const hub = await fetchCommunityPlatform(profile.community_id)
    if (isBasicCommunity(hub)) {
      writeLastPortal('community')
      if (!blockAutoRedirect) {
        const payIntent = readQuickUploadIntent()
        navigate(
          quickUploadWantsPayment(payIntent)
            ? `/upload/pay?pack=${encodeURIComponent(payIntent.packageId || 'game_1')}`
            : HUB_HOME,
          { replace: true },
        )
      }
      return true
    }
  }

  // Phone only for managers who already have a community (returning users).
  if (profile?.community_id && !profileHasPhone(profile) && !skipPhoneCheck) {
    stashPhoneReturn('community')
    navigate('/verify-phone')
    return true
  }

  if (profile?.community_id && isCommunityManager(profile)) {
    writeLastPortal('community')

    const payIntent = readQuickUploadIntent()
    const resumePayment = quickUploadWantsPayment(payIntent)
    const openUploadedGame = payIntent?.stage === 'ready'

    // Navigate immediately so the login screen never sits behind a slow
    // communities lookup (common after OAuth when the socket is cold).
    if (!blockAutoRedirect) {
      navigate(
        resumePayment
          ? `/community/buy-credits?pack=${encodeURIComponent(payIntent.packageId || 'game_1')}`
          : openUploadedGame
            ? (payIntent.matchId ? `/community/match/${payIntent.matchId}` : '/community/schedule')
            : communityHomeRoute(),
        { replace: true },
      )
    }

    let communityData = null
    try {
      const { data, error } = await supabase
        .from('communities')
        .select('name')
        .eq('id', profile.community_id)
        .maybeSingle()
      if (!error) communityData = data
    } catch (err) {
      console.warn('routeCommunityPortalAfterAuth: communities lookup failed', err)
    }

    persistCommunityContext({
      communityName: communityData?.name,
      managerName: profile.name,
      userId,
    })
    return true
  }

  return false
}

/** @returns {Promise<boolean>} true if navigation was handled */
export async function routeAcademyPortalAfterAuth(
  navigate,
  profile,
  { blockAutoRedirect = false, userId, skipPhoneCheck = false } = {},
) {
  const leagueJoinToken = peekAcademyLeagueJoinToken()
  if (leagueJoinToken) {
    writeLastPortal('academy')
    if (!blockAutoRedirect) {
      navigate(buildAcademyLeagueJoinPath(leagueJoinToken), { replace: true })
    }
    return true
  }

  if (profile?.community_id && isCommunityManager(profile) && !profile?.academy_id) {
    return false
  }

  if (needsSignupIntakeOnly('academy', profile)) {
    writeLastPortal('academy')
    const pendingJoinId = peekAcademyJoinId()
    if (pendingJoinId && userId) {
      if (!blockAutoRedirect) {
        navigate(academyInviteSignInPath(pendingJoinId), { replace: true })
      }
      return true
    }
    await navigateToSignupIntake(navigate, 'academy')
    return true
  }

  if (profile?.academy_id && !profileHasPhone(profile) && !skipPhoneCheck) {
    stashPhoneReturn('academy')
    navigate('/verify-phone')
    return true
  }

  if (profile?.academy_id && isCoach(profile)) {
    const academyName =
      profile.academies?.name ??
      (await supabase
        .from('academies')
        .select('name')
        .eq('id', profile.academy_id)
        .single()).data?.name
    persistAcademyContext({
      academyName,
      coachName: profile.name,
      userId,
    })
    writeLastPortal('academy')
    clearDraft()
    if (!blockAutoRedirect) {
      navigate('/academy/dashboard', { replace: true })
    }
    return true
  }

  return false
}

/** @returns {Promise<boolean>} true if navigation was handled */
export async function routeVenuePortalAfterAuth(
  navigate,
  profile,
  { blockAutoRedirect = false, userId, skipPhoneCheck = false } = {},
) {
  if (needsSignupIntakeOnly('venue', profile)) {
    writeLastPortal('venue')
    await navigateToSignupIntake(navigate, 'venue')
    return true
  }

  if (profile && !profileHasPhone(profile) && !skipPhoneCheck) {
    stashPhoneReturn('venue')
    navigate('/verify-phone')
    return true
  }

  if (isVenueOwner(profile)) {
    writeLastPortal('venue')
    if (!blockAutoRedirect) {
      navigate('/venue-owner', { replace: true })
    }
    return true
  }

  return false
}
